import { useState, useEffect, useRef } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useSelector } from "react-redux";
import { FunFact } from "../noc/FunFact";
import axios from "axios";
import { DateTime } from "luxon";

import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  FormControl,
  FormControlLabel,
  Grid2 as Grid,
  Radio,
  RadioGroup,
  Stack,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import { LoadingButton } from "@mui/lab";
import LottieAnimation from "../../animation/LottieAnimation";
import {
  formatAcceptanceTime,
  toTitleCaseName,
} from "./acceptanceDisplayHelpers";

const AcceptInstant = () => {
  const navigate = useNavigate();
  const sessions = useSelector((state) => state.sessions);
  const location = useLocation();
  const theme = useTheme();
  const queryParams = new URLSearchParams(location.search);

  const notaryIDQuery = queryParams.get("ni");
  const instantTokenQuery = queryParams.get("it");
  const taskIdQuery = queryParams.get("ti");
  const signeeTokenQuery = queryParams.get("st");
  const reasonQuery = queryParams.get("reason");
  const expiredRequestIdQuery = queryParams.get("eid");
  const fromExpiredQuery = queryParams.get("fe");
  const requiresSigneeToken = reasonQuery === "owner";

  const mobileSize = useMediaQuery(theme.breakpoints.down("sm"));
  const tabletSize = useMediaQuery(theme.breakpoints.down("md"));

  const [pageLoading, setPageLoading] = useState(true);
  const [isSuccess, setIsSuccess] = useState(null);
  const [errorMessage, setErrorMessage] = useState("Please Wait");
  const [errorType, setErrorType] = useState("waiting");
  const [acceptedByNotaryName, setAcceptedByNotaryName] = useState("");
  const [acceptedAt, setAcceptedAt] = useState("");
  const [funData, setFunData] = useState([]);
  const [appointment, setAppointment] = useState(null);
  const [session_url, setSession_url] = useState(null);
  const [timezone] = useState("America/New_York");
  const [sessionReady, setSessionReady] = useState(false);
  const [wsConnected, setWsConnected] = useState(false);
  const [challengeToken, setChallengeToken] = useState("");
  const [signerDetails, setSignerDetails] = useState(null);
  const [pastNotarizations, setPastNotarizations] = useState([]);
  const [personallyKnown, setPersonallyKnown] = useState("no");
  const [acceptLoading, setAcceptLoading] = useState(false);
  const canJoinSession = Boolean(session_url);
  const hasValidated = useRef(false);
  const acceptanceWsRef = useRef(null);
  const deletedExpiredRequestRef = useRef(false);
  const reconnectAttemptsRef = useRef(0);
  const reconnectTimerRef = useRef(null);
  const recoveryPollRef = useRef(null);
  const acceptingInProgressRef = useRef(false);
  const duplicateRef = useRef(false);
  const componentMountedRef = useRef(true);
  const intentionalWsCloseRef = useRef(false);
  const hasRecoveredOnConnectRef = useRef(false);

  const authMethod = personallyKnown === "yes" ? "skip" : "biometrics";
  const maxAcceptanceReconnectAttempts = 4;

  const clearAcceptanceReconnectTimer = () => {
    if (reconnectTimerRef.current) {
      clearTimeout(reconnectTimerRef.current);
      reconnectTimerRef.current = null;
    }
  };

  const stopRecoveryPolling = () => {
    if (recoveryPollRef.current) {
      clearInterval(recoveryPollRef.current);
      recoveryPollRef.current = null;
    }
  };

  const parseAppointmentToDisplay = (appointmentValue) => {
    if (!appointmentValue) return null;

    // New/ideal format: ISO string
    if (typeof appointmentValue === "string") {
      const dt = DateTime.fromISO(appointmentValue, { zone: timezone });
      return dt.isValid ? dt.toFormat("cccc, LLLL dd, yyyy hh:mm a") : null;
    }

    // Legacy/recovery format: booked array (Mongo stored object list)
    if (Array.isArray(appointmentValue) && appointmentValue.length > 0) {
      const raw = appointmentValue[0] || {};
      if (raw.year && raw.month && raw.day_of_month && raw.time) {
        const dt = DateTime.fromObject(
          {
            year: raw.year,
            month: raw.month,
            day: raw.day_of_month,
            hour: parseInt(String(raw.time).split(":")[0] || "0", 10),
            minute: parseInt(String(raw.time).split(":")[1] || "0", 10),
          },
          { zone: raw.timezone || "UTC" },
        ).setZone(timezone);
        return dt.isValid ? dt.toFormat("cccc, LLLL dd, yyyy hh:mm a") : null;
      }
    }

    return null;
  };

  const computeSessionReady = (appointmentValue) => {
    if (!appointmentValue) return false;

    try {
      let appointmentTimeUTC = null;

      if (typeof appointmentValue === "string") {
        appointmentTimeUTC = DateTime.fromISO(appointmentValue, {
          zone: "UTC",
        });
      } else if (
        Array.isArray(appointmentValue) &&
        appointmentValue.length > 0
      ) {
        const raw = appointmentValue[0] || {};
        if (raw.year && raw.month && raw.day_of_month && raw.time) {
          appointmentTimeUTC = DateTime.fromObject(
            {
              year: raw.year,
              month: raw.month,
              day: raw.day_of_month,
              hour: parseInt(String(raw.time).split(":")[0] || "0", 10),
              minute: parseInt(String(raw.time).split(":")[1] || "0", 10),
            },
            { zone: raw.timezone || "UTC" },
          ).setZone("UTC");
        }
      }

      if (!appointmentTimeUTC || !appointmentTimeUTC.isValid) return false;

      const currentTimeUTC = DateTime.now().setZone("UTC");
      const difference = appointmentTimeUTC.diff(
        currentTimeUTC,
        "minutes",
      ).minutes;
      return (difference > 0 && difference <= 10) || difference < 0;
    } catch (e) {
      return false;
    }
  };

  const checkFinalizeState = async (notarizetype) => {
    if (notarizetype !== "process") return;
    try {
      const resp = await axios.post(
        `${process.env.REACT_APP_BASE_URL}/nocApi/notary/check-finalize-state`,
        {
          instant_token: instantTokenQuery,
          notary_id: notaryIDQuery,
          task_id: taskIdQuery,
          reason: reasonQuery,
        },
        { withCredentials: true },
      );

      if (resp?.data?.type === "process") {
        return false;
      } else if (
        resp?.data?.type === "accepted" ||
        resp?.data?.type === "taken" ||
        resp?.data?.type === "completed"
      ) {
        duplicateRef.current = false;
        if (resp.data.type === "accepted") {
          await applyAcceptanceState({ status: "accepted", ...resp.data });
        } else {
          applyUnavailableState(resp.data);
        }
        return true;
      } else if (
        resp?.data?.type === "waiting" ||
        resp?.data?.type === "processing"
      ) {
        return false;
      }
    } catch (error) {
      setErrorMessage(error?.response?.data?.message);
      setErrorType(error?.response?.data?.type);
      if (error?.response?.data?.type === "unauthorized") navigate("/login");
    }
  };

  const applyAcceptanceState = async (state = {}) => {
    if (!state) return false;

    if (state.status === "accepted") {
      console.info("[AcceptInstant] Applying accepted state", state);
      acceptingInProgressRef.current = false;
      setIsSuccess(true);
      setErrorMessage(state.message || "Job has been accepted successfully");
      setErrorType(state.type || "accepted");
      if (state.session_url) {
        setSession_url(state.session_url);
        setSessionReady(true);
      }
      await cleanupExpiredRequest();

      try {
        const displayAppointment = parseAppointmentToDisplay(
          state.appointment_iso || state.appointment,
        );
        if (displayAppointment) {
          setAppointment(displayAppointment);
        }
        setSessionReady(
          Boolean(state.session_url) ||
            computeSessionReady(state.appointment_iso || state.appointment),
        );
      } catch (e) {
        // Never allow appointment parsing failure to keep polling alive.
        console.error("[AcceptInstant] Failed to parse appointment", e);
      }

      stopRecoveryPolling();
      clearAcceptanceReconnectTimer();
      reconnectAttemptsRef.current = 0;

      if (acceptanceWsRef.current?.readyState === WebSocket.OPEN) {
        intentionalWsCloseRef.current = true;
        acceptanceWsRef.current.close();
      }

      return true;
    }

    if (state.status === "processing") {
      console.info("[AcceptInstant] Applying processing state", state);
      const signeeConnected = state.signee_connected !== false;
      setErrorMessage(
        state.message ||
          (signeeConnected
            ? "Processing your acceptance..."
            : "Signer is offline. Processing will continue automatically and may take a bit longer."),
      );
      setErrorType(signeeConnected ? "waiting" : "processing");
    }

    if (state.type === "completed" || state.type === "taken") {
      applyUnavailableState(state);
      return true;
    }

    if (state.status === "failed" || state.status === "stop") {
      console.info("[AcceptInstant] Applying terminal failure state", state);
      setIsSuccess(false);
      setErrorMessage(state.message || "Unable to accept this job.");
      setErrorType(state.type || "failed");

      stopRecoveryPolling();
      clearAcceptanceReconnectTimer();
      reconnectAttemptsRef.current = 0;
      acceptingInProgressRef.current = false;

      if (acceptanceWsRef.current?.readyState === WebSocket.OPEN) {
        acceptanceWsRef.current.close();
      }

      // Return true so recovery polling stops.
      return true;
    }

    return false;
  };

  const isCurrentAcceptanceEvent = (data = {}) =>
    Boolean(
      data &&
        data.task_id === taskIdQuery &&
        (!data.instant_token || data.instant_token === instantTokenQuery) &&
        (!data.notary_id || data.notary_id === notaryIDQuery),
    );

  const applyUnavailableState = (data = {}) => {
    acceptingInProgressRef.current = false;
    duplicateRef.current = false;
    stopRecoveryPolling();
    clearAcceptanceReconnectTimer();
    setIsSuccess(false);
    setSession_url(null);
    setSessionReady(false);
    setAppointment(null);
    setAcceptedByNotaryName(
      data.type === "taken"
        ? toTitleCaseName(data.accepted_by_notary_name)
        : "",
    );
    setAcceptedAt(
      data.type === "taken"
        ? formatAcceptanceTime(data.accepted_at, timezone)
        : "",
    );
    setErrorMessage(
      data.type === "taken"
        ? data.accepted_by_notary_name
          ? `This appointment was accepted by ${toTitleCaseName(data.accepted_by_notary_name)}.`
          : "This appointment was accepted by another notary."
        : data.message || "Unable to accept this job.",
    );
    setErrorType(data.type || "failed");
  };

  const recoverAcceptanceState = async () => {
    if (!instantTokenQuery || !taskIdQuery) return false;
    if (!acceptingInProgressRef.current) return false;

    try {
      const response = await axios.post(
        `${process.env.REACT_APP_BASE_URL}/nocApi/notary/recover-acceptance-state`,
        {
          instant_token: instantTokenQuery,
          task_id: taskIdQuery,
          user_id: sessions?.notarySession?.user_id || "",
          session_type: "notarySession",
          notary_id: notaryIDQuery,
          reason: reasonQuery,
        },
        { withCredentials: true },
      );

      if (response.data.success && response.data.recovery_data?.current_state) {
        console.info(
          "[AcceptInstant] Recovery response",
          response.data.recovery_data.current_state,
        );
        return await applyAcceptanceState(
          response.data.recovery_data.current_state,
        );
      }
    } catch (error) {
      console.error("Failed to recover connection state:", error);
    }

    return false;
  };

  const ensureRecoveryPolling = () => {
    if (recoveryPollRef.current || !acceptingInProgressRef.current) return;

    recoveryPollRef.current = setInterval(async () => {
      if (!componentMountedRef.current || !acceptingInProgressRef.current) {
        stopRecoveryPolling();
        return;
      }
      const recovered = await recoverAcceptanceState();
      if (recovered) {
        stopRecoveryPolling();
      }
    }, 4000);
  };

  const ensureRecoveryPollingFinalize = (notarizetype) => {
    if (notarizetype !== "process") return;
    if (recoveryPollRef.current || !duplicateRef.current) return;

    recoveryPollRef.current = setInterval(async () => {
      if (!duplicateRef.current) {
        stopRecoveryPolling();
        return;
      }
      const recovered = await checkFinalizeState(notarizetype);
      if (recovered) {
        stopRecoveryPolling();
      }
    }, 4000);
  };

  const establishAcceptanceWebSocket = () => {
    if (!instantTokenQuery || !notaryIDQuery) return null;

    const existingSocket = acceptanceWsRef.current;
    if (
      existingSocket &&
      (existingSocket.readyState === WebSocket.OPEN ||
        existingSocket.readyState === WebSocket.CONNECTING)
    ) {
      return existingSocket;
    }

    const wsUrl = `${process.env.REACT_APP_WS_URL}/nocApi_ws/notary_acceptance/${instantTokenQuery}/${notaryIDQuery}`;

    try {
      const ws = new WebSocket(wsUrl);
      acceptanceWsRef.current = ws;

      ws.onmessage = async (event) => {
        try {
          const message = JSON.parse(event.data);

          if (
            message.event === "instant_ron_processing" &&
            isCurrentAcceptanceEvent(message.data)
          ) {
            console.info(
              "[AcceptInstant] Acceptance websocket processing",
              message,
            );
            await applyAcceptanceState({
              status: "processing",
              ...message.data,
            });
          } else if (
            message.event === "instant_ron_accepted" &&
            isCurrentAcceptanceEvent(message.data)
          ) {
            console.info(
              "[AcceptInstant] Acceptance websocket accepted",
              message,
            );
            await applyAcceptanceState({
              status: "accepted",
              ...message.data,
            });
          }
        } catch (error) {
          console.error("Error parsing WebSocket message:", error);
        }
      };

      ws.onerror = (error) => console.error("WebSocket error:", error);

      ws.onopen = () => {
        intentionalWsCloseRef.current = false;
        setWsConnected(true);
        reconnectAttemptsRef.current = 0;
        clearAcceptanceReconnectTimer();

        // Only recover when there is an active acceptance flow.
        // Do not tie this to the main notary session websocket, otherwise
        // unrelated Redux session updates can keep re-triggering recovery.
        if (
          acceptingInProgressRef.current &&
          !hasRecoveredOnConnectRef.current &&
          isSuccess !== true
        ) {
          hasRecoveredOnConnectRef.current = true;
          recoverAcceptanceState();
        }
      };

      ws.onclose = () => {
        acceptanceWsRef.current = null;
        setWsConnected(false);

        if (intentionalWsCloseRef.current) {
          intentionalWsCloseRef.current = false;
          return;
        }

        if (!acceptingInProgressRef.current || isSuccess === true) {
          return;
        }

        ensureRecoveryPolling();

        if (reconnectAttemptsRef.current >= maxAcceptanceReconnectAttempts) {
          return;
        }

        const attempt = reconnectAttemptsRef.current + 1;
        reconnectAttemptsRef.current = attempt;
        clearAcceptanceReconnectTimer();
        reconnectTimerRef.current = setTimeout(
          () => {
            if (!componentMountedRef.current || !acceptingInProgressRef.current)
              return;
            establishAcceptanceWebSocket();
          },
          Math.min(1500 * attempt, 6000),
        );
      };

      return ws;
    } catch (error) {
      console.error("Failed to establish WebSocket:", error);
      ensureRecoveryPolling();
      return null;
    }
  };

  const formatPastNotarizationDate = (rawDate) => {
    if (!rawDate) return "N/A";

    if (typeof rawDate === "number") {
      return DateTime.fromSeconds(rawDate)
        .setZone(timezone)
        .toFormat("cccc, LLLL dd, yyyy hh:mm a");
    }

    const parsed = DateTime.fromISO(rawDate).setZone(timezone);
    if (parsed.isValid) {
      return parsed.toFormat("cccc, LLLL dd, yyyy hh:mm a");
    }

    return rawDate;
  };

  const signerType = signerDetails?.signer_type
    ? signerDetails.signer_type.charAt(0).toUpperCase() +
      signerDetails.signer_type.slice(1)
    : "N/A";

  const fullName =
    signerDetails?.personal?.full_name ||
    `${signerDetails?.personal?.first || ""} ${
      signerDetails?.personal?.last || ""
    }`.trim() ||
    "N/A";

  const email = signerDetails?.personal?.email || "N/A";
  const phone = signerDetails?.personal?.mobile || "N/A";

  const columns = [
    { field: "date", headerName: "Completed", flex: 1, minWidth: 180 },
    { field: "task_id", headerName: "Task ID", flex: 1, minWidth: 160 },
    { field: "signer_type", headerName: "Signer", flex: 1, minWidth: 120 },
    { field: "notary_name", headerName: "Notary", flex: 1, minWidth: 160 },
    {
      field: "notary_email",
      headerName: "Notary Email",
      flex: 1,
      minWidth: 200,
    },
  ];

  const rows = (pastNotarizations || []).map((record, index) => ({
    id: `${record?.task_id || "task"}-${record?.completed_at || index}`,
    date: formatPastNotarizationDate(record?.completed_at || record?.date),
    task_id: record?.task_id || "N/A",
    signer_type: record?.signer_type
      ? record.signer_type.charAt(0).toUpperCase() + record.signer_type.slice(1)
      : "N/A",
    notary_name: record?.notary?.notary_name || "N/A",
    notary_email: record?.notary?.email || "N/A",
  }));

  const requestAcceptanceChallenge = async () => {
    try {
      const resp = await axios.post(
        `${process.env.REACT_APP_BASE_URL}/nocApi/notary/create-acceptance-challenge`,
        {
          instant_token: instantTokenQuery,
          notary_id: notaryIDQuery,
          task_id: taskIdQuery,
          reason: reasonQuery,
        },
        { withCredentials: true },
      );

      if (resp?.data?.success && resp?.data?.challenge_token) {
        setChallengeToken(resp.data.challenge_token);
        return resp.data.challenge_token;
      }

      setErrorMessage(
        resp?.data?.message ||
          "Unable to verify acceptance request. Please open the link directly in your browser.",
      );
      setErrorType(resp?.data?.type || "failed");
      setIsSuccess(false);
      return null;
    } catch (error) {
      setErrorMessage(
        error?.response?.data?.message ||
          "Unable to start secure acceptance flow. Please refresh and try again.",
      );
      setErrorType(error?.response?.data?.type || "failed");
      setIsSuccess(false);
      return null;
    }
  };

  const cleanupExpiredRequest = async () => {
    if (!expiredRequestIdQuery || deletedExpiredRequestRef.current) {
      return;
    }

    try {
      const currentSession = sessions?.currentSession || "notarySession";
      await axios.post(
        `${process.env.REACT_APP_BASE_URL}/nocApi/notary/delete-expired-ron-request`,
        {
          expired_request_id: expiredRequestIdQuery,
          currentSession,
        },
        { withCredentials: true },
      );
      deletedExpiredRequestRef.current = true;
    } catch (error) {
      // Best effort cleanup: do not block successful acceptance flow.
      console.error("Failed to delete expired expedited request row:", error);
    }
  };

  const handleAcceptInstant = async () => {
    setAcceptLoading(true);
    setErrorMessage("Processing your acceptance...");
    setErrorType("waiting");
    acceptingInProgressRef.current = true;
    reconnectAttemptsRef.current = 0;
    clearAcceptanceReconnectTimer();

    try {
      let challenge = challengeToken;
      if (!challenge) {
        challenge = await requestAcceptanceChallenge();
      }

      if (!challenge) {
        setAcceptLoading(false);
        return;
      }

      const tokenResponse = await axios.post(
        `${process.env.REACT_APP_BASE_URL}/nocApi/notary/accept-instant`,
        {
          instant_token: instantTokenQuery,
          notary_id: notaryIDQuery,
          task_id: taskIdQuery,
          signee_token: signeeTokenQuery,
          reason: reasonQuery,
          challenge_token: challenge,
          authMethod,
        },
        { withCredentials: true },
      );

      if (tokenResponse.data.success === true) {
        if (tokenResponse.data.type === "accepted") {
          await applyAcceptanceState({
            status: "accepted",
            ...tokenResponse.data,
          });
        } else {
          setErrorMessage("Processing your acceptance...");
          setErrorType("waiting");
          await recoverAcceptanceState();
          ensureRecoveryPolling();
        }
      } else if (tokenResponse.data.success === false) {
        if (tokenResponse?.data?.type === "scanner_blocked") {
          acceptingInProgressRef.current = false;
          stopRecoveryPolling();
          clearAcceptanceReconnectTimer();
          setErrorMessage(tokenResponse?.data?.message);
          setErrorType("failed");
          setIsSuccess(false);
          return;
        } else if (
          tokenResponse?.data?.type === "taken" ||
          tokenResponse?.data?.type === "completed"
        ) {
          applyUnavailableState(tokenResponse.data);
          return;
        } else {
          acceptingInProgressRef.current = false;
          stopRecoveryPolling();
          clearAcceptanceReconnectTimer();
          setErrorMessage(tokenResponse?.data?.message);
          setErrorType(tokenResponse?.data?.type);
          setIsSuccess(false);
          return;
        }
      } else {
        setErrorMessage("Processing your acceptance...");
        setErrorType("waiting");
        await recoverAcceptanceState();
        ensureRecoveryPolling();
      }

      establishAcceptanceWebSocket();
    } catch (error) {
      acceptingInProgressRef.current = false;
      stopRecoveryPolling();
      clearAcceptanceReconnectTimer();
      setErrorMessage(error?.response?.data?.message || "Unable to accept job");
      setErrorType(error?.response?.data?.type || "failed");
      if (error?.response?.data?.type === "unauthorized") {
        navigate("/login");
      }
    } finally {
      setAcceptLoading(false);
    }
  };

  useEffect(() => {
    return () => {
      componentMountedRef.current = false;
      clearAcceptanceReconnectTimer();
      stopRecoveryPolling();
      if (acceptanceWsRef.current) {
        try {
          acceptanceWsRef.current.onclose = null;
          acceptanceWsRef.current.close();
        } catch {}
      }
    };
  }, []);

  useEffect(() => {
    if (isSuccess === true) {
      acceptingInProgressRef.current = false;
      hasRecoveredOnConnectRef.current = true;
      stopRecoveryPolling();
      clearAcceptanceReconnectTimer();
      const timert = setTimeout(() => navigate("/login"), 300000);
      return () => clearTimeout(timert);
    }
  }, [isSuccess, navigate]);

  useEffect(() => {
    let funCount = 0;
    const getFunFacts = async () => {
      const resp = await axios.get(
        `${process.env.REACT_APP_BASE_URL}/nocApi/data/funFacts`,
      );
      setFunData(resp.data.data.funFacts);
    };
    if (funCount < 1) {
      getFunFacts();
      funCount += 1;
    }
  }, []);

  const validate = async () => {
    try {
      setPageLoading(true);
      const resp = await axios.post(
        `${process.env.REACT_APP_BASE_URL}/nocApi/notary/check-finalize-state`,
        {
          instant_token: instantTokenQuery,
          notary_id: notaryIDQuery,
          task_id: taskIdQuery,
          reason: reasonQuery,
        },
        { withCredentials: true },
      );

      if (resp?.data?.type === "process") {
        const detailsResponse = await axios.post(
          `${process.env.REACT_APP_BASE_URL}/nocApi/notary/accept-instant-details`,
          {
            instant_token: instantTokenQuery,
            notary_id: notaryIDQuery,
            task_id: taskIdQuery,
            signee_token: signeeTokenQuery,
            reason: reasonQuery,
            from_expired: fromExpiredQuery,
          },
          { withCredentials: true },
        );

        if (detailsResponse?.data?.success) {
          setSignerDetails(detailsResponse?.data?.current_signer || null);
          setPastNotarizations(detailsResponse?.data?.past_notarizations || []);

          duplicateRef.current = true;
          establishAcceptanceWebSocket();
          await requestAcceptanceChallenge();
          ensureRecoveryPollingFinalize(resp?.data?.type);

          setErrorType("review");
        } else {
          setErrorMessage(
            detailsResponse?.data?.message || "Unable to load signer details",
          );
          setErrorType(detailsResponse?.data?.type || "failed");
          setIsSuccess(false);
        }
      } else {
        setErrorMessage(resp?.data?.message);
        setErrorType(resp?.data?.type);

        if (resp?.data?.type === "accepted") {
          await applyAcceptanceState({ status: "accepted", ...resp.data });
        } else if (
          resp?.data?.type === "waiting" ||
          resp?.data?.type === "processing"
        ) {
          acceptingInProgressRef.current = true;
          establishAcceptanceWebSocket();
          await recoverAcceptanceState();
          ensureRecoveryPolling();
        } else {
          applyUnavailableState(resp.data);
        }
      }

      setPageLoading(false);
    } catch (error) {
      setErrorMessage(error?.response?.data?.message);
      setErrorType(error?.response?.data?.type);
      if (error?.response?.data?.type === "unauthorized") navigate("/login");
      setPageLoading(false);
    }
  };

  useEffect(() => {
    if (
      !instantTokenQuery ||
      !notaryIDQuery ||
      !taskIdQuery ||
      !reasonQuery ||
      (requiresSigneeToken && !signeeTokenQuery)
    ) {
      navigate("/");
      return;
    }

    if (!hasValidated.current) {
      hasValidated.current = true;
      validate();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    notaryIDQuery,
    instantTokenQuery,
    taskIdQuery,
    signeeTokenQuery,
    reasonQuery,
    requiresSigneeToken,
    timezone,
  ]);

  // Redux websocket message fallback handling
  const notarySession = useSelector((state) => state.notarySession);

  useEffect(() => {
    if (notarySession?.lastWebSocketMessage) {
      const message = notarySession.lastWebSocketMessage;

      if (
        message.event === "instant_ron_processing" &&
        isCurrentAcceptanceEvent(message.data)
      ) {
        const responseData = message.data || {};
        console.info("[AcceptInstant] Redux websocket processing", message);
        const signeeConnected = responseData.signee_connected !== false;
        setErrorMessage(
          responseData.message ||
            (signeeConnected
              ? "Processing your acceptance..."
              : "Signer is offline. Processing will continue automatically and may take a bit longer."),
        );
        setErrorType(signeeConnected ? "waiting" : "processing");
      } else if (
        message.event === "instant_ron_accepted" &&
        isCurrentAcceptanceEvent(message.data)
      ) {
        console.info("[AcceptInstant] Redux websocket accepted", message);
        const responseData = message.data;
        applyAcceptanceState({
          status: "accepted",
          ...responseData,
        });
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [notarySession?.lastWebSocketMessage, timezone]);

  useEffect(() => {
    const handleBeforeUnload = () => {
      clearAcceptanceReconnectTimer();
      stopRecoveryPolling();
      if (acceptanceWsRef.current?.readyState === WebSocket.OPEN) {
        acceptanceWsRef.current.close();
      }
    };

    window.addEventListener("beforeunload", handleBeforeUnload);
    return () => window.removeEventListener("beforeunload", handleBeforeUnload);
  }, []);

  // ===== Layout helpers (fixed-height + scroll + sticky header/footer) =====
  const shellBgSx = mobileSize
    ? {
        display: "flex",
        alignItems: "flex-start",
        justifyContent: "center",
        overflowY: "auto",
        overflowX: "hidden",
        width: "100%",
        minHeight: "100vh",
        bgcolor: "#fff",
        "@supports (height: 100dvh)": {
          minHeight: "100dvh",
        },
      }
    : {
        backgroundImage: "url('/img/website_img/homeImg.jpeg')",
        backgroundRepeat: "repeat",
        width: "100%",
        height: "100vh",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
      };

  const mainCardSx = tabletSize
    ? {
        width: "100%",
        height: "100vh",
        borderRadius: 0,
        display: "flex",
        flexDirection: "column",
        overflow: "hidden",
      }
    : {
        width: "100%",
        maxWidth: 980,
        height: "90vh", // fixed height (desktop)
        borderRadius: 4,
        display: "flex",
        flexDirection: "column",
        overflow: "hidden",
        boxShadow: "0 10px 30px rgba(0,0,0,0.08)",
      };

  return (
    <>
      {pageLoading ? (
        <Box
          sx={{
            display: "flex",
            width: "100%",
            height: "100vh",
            justifyContent: "center",
            alignItems: "center",
            flexDirection: "column",
          }}
        >
          {tabletSize ? (
            <>
              <Box sx={{ mb: 2 }}>
                <img
                  style={{
                    width: "10rem",
                    marginLeft: "auto",
                    marginRight: "auto",
                  }}
                  alt="Permit Rockstar Logo"
                  src="/img/logos/logo_main.png"
                />
              </Box>
              <LottieAnimation />
            </>
          ) : (
            <CircularProgress color="warning" size="10rem" />
          )}
        </Box>
      ) : (
        <Box sx={shellBgSx}>
          {/* ===== SUCCESS / WAITING / FAILED STATES (kept as-is visually, but centered) ===== */}
          {errorType !== "review" ? (
            <Box
              sx={{
                width: "100%",
                maxWidth: tabletSize ? "100%" : 980,
                boxSizing: "border-box",
                bgcolor: "#fff",
                borderRadius: tabletSize ? 0 : 4,
                px: mobileSize ? 2 : 3,
                py: mobileSize ? 1.5 : 2,
                minHeight: mobileSize ? "100dvh" : "auto",
                maxHeight: tabletSize ? "none" : "90vh",
                overflowY: "auto",
              }}
            >
              <Box
                sx={{ display: "flex", justifyContent: "center", mb: 2 }}
                onClick={() => navigate("/")}
              >
                <img
                  style={{
                    maxWidth: mobileSize
                      ? "15rem"
                      : tabletSize
                        ? "50%"
                        : "10rem",
                    width: "100%",
                    height: "auto",
                    cursor: "pointer",
                  }}
                  alt="Permit Rockstar Logo"
                  src="/img/logos/logo_main.png"
                />
              </Box>

              {isSuccess && isSuccess === true ? (
                <>
                  <Typography
                    color="#4CAF50"
                    variant="h5"
                    sx={{ mb: 2, mt: 1 }}
                    align="center"
                  >
                    {errorMessage ||
                      "Documents have been validated and the appointment for the RON has been confirmed."}
                  </Typography>

                  {appointment && (
                    <>
                      <Typography
                        color="primary"
                        variant="h5"
                        fontWeight="bold"
                        align="center"
                        sx={{ mb: 1 }}
                      >
                        Session Starts at:
                      </Typography>
                      <Typography
                        color="primary"
                        variant="h4"
                        fontWeight="bold"
                        align="center"
                        sx={{ mb: 3 }}
                      >
                        {appointment}
                      </Typography>
                    </>
                  )}

                  <Grid container justifyContent="center">
                    <Grid size={{ xs: 12 }}>
                      <Button
                        size="large"
                        component={canJoinSession ? "a" : "button"}
                        href={canJoinSession ? session_url : undefined}
                        target={canJoinSession ? "_blank" : undefined}
                        rel={canJoinSession ? "noopener noreferrer" : undefined}
                        onClick={
                          canJoinSession ? undefined : () => navigate("/login")
                        }
                        fullWidth
                        variant="contained"
                      >
                        {canJoinSession ? "Join Session" : "Back to Login"}
                      </Button>
                    </Grid>
                  </Grid>
                </>
              ) : errorType === "waiting" ? (
                <>
                  <Typography
                    color="primary"
                    variant="h5"
                    sx={{ mb: 2, mt: 1 }}
                    align="center"
                  >
                    Accepting Remote Online Notarization (RON) Appointment!
                  </Typography>
                  <Typography
                    color="primary"
                    variant="h5"
                    sx={{ mb: 2, mt: 1 }}
                    align="center"
                  >
                    {errorMessage}
                  </Typography>
                  <Typography
                    color="primary"
                    variant="body1"
                    align="center"
                    sx={{ mb: 3 }}
                  >
                    We are ensuring the document/s can be notarized to ensure a
                    smooth experience. Once we validate the documents, you will
                    be assigned the job and can start the session.
                  </Typography>
                  <Typography
                    color="primary"
                    variant="body1"
                    align="center"
                    sx={{ mb: 3, fontWeight: "bold" }}
                  >
                    In the meantime, did you know...
                  </Typography>
                  <FunFact type="accept" data={funData} />
                  <Grid container justifyContent="center" mt={3}>
                    <Grid size={{ xs: 12 }}>
                      <LoadingButton
                        loading={true}
                        fullWidth
                        variant="contained"
                      >
                        Please Wait
                      </LoadingButton>
                    </Grid>
                  </Grid>
                </>
              ) : errorType === "taken" ? (
                <Stack
                  spacing={mobileSize ? 2 : 2.5}
                  alignItems="center"
                  sx={{ width: "100%", mx: "auto" }}
                >
                  <Typography
                    color="error"
                    variant={mobileSize ? "h5" : "h4"}
                    sx={{
                      fontWeight: 800,
                      lineHeight: 1.2,
                      overflowWrap: "anywhere",
                    }}
                    align="center"
                  >
                    Remote Online Notarization (RON) Appointment is Unavailable!
                  </Typography>

                  <Box sx={{ textAlign: "center", width: "100%" }}>
                    <Typography
                      color="primary"
                      variant={mobileSize ? "h6" : "h5"}
                      sx={{ fontWeight: 800, lineHeight: 1.3 }}
                    >
                      This appointment was accepted by{" "}
                      {acceptedByNotaryName || "another notary"}.
                    </Typography>
                    {acceptedAt && (
                      <Typography
                        color="text.secondary"
                        variant="body1"
                        sx={{ mt: 1, fontWeight: 700 }}
                      >
                        Accepted on {acceptedAt}.
                      </Typography>
                    )}
                  </Box>

                  <Typography
                    color="primary"
                    variant="body1"
                    align="center"
                    sx={{
                      lineHeight: 1.6,
                      maxWidth: 760,
                      overflowWrap: "anywhere",
                    }}
                  >
                    We regret to inform you that the Remote Online Notarization
                    (RON) appointment you selected has already been accepted by
                    you or another notary. We appreciate your prompt response
                    and encourage you to log into your dashboard for other
                    available notarization opportunities.
                  </Typography>

                  <Button
                    onClick={() => navigate("/")}
                    fullWidth
                    variant="contained"
                    sx={{ mt: 1, minHeight: 44 }}
                  >
                    BACK TO MAIN MENU
                  </Button>
                </Stack>
              ) : (
                <>
                  <Typography
                    color={errorType === "processing" ? "primary" : "error"}
                    variant="h5"
                    sx={{ mb: 2, mt: 1 }}
                    align="center"
                  >
                    {errorType === "processing"
                      ? "Processing RON Appointment Acceptance"
                      : errorType === "completed"
                        ? "RON Appointment Has Been Completed"
                        : "Remote Online Notarization (RON) Appointment is Unavailable!"}
                  </Typography>

                  <Typography
                    color="primary"
                    variant="h5"
                    sx={{ mb: 2, mt: 1 }}
                    align="center"
                  >
                    {errorMessage}
                  </Typography>

                  <Typography
                    color="primary"
                    variant="body1"
                    align="center"
                    sx={{ mb: 3 }}
                  >
                    {errorType === "processing" ? (
                      <Box
                        sx={{
                          display: "flex",
                          flexDirection: "column",
                          alignItems: "center",
                          gap: 2,
                        }}
                      >
                        <CircularProgress size={40} color="primary" />
                        <span>
                          Please wait while we process your acceptance and
                          prepare the notarization documents. This may take a
                          few moments.
                        </span>
                      </Box>
                    ) : (
                      `We regret to inform you ${
                        errorType === "completed"
                          ? "that this Remote Online Notarization (RON) appointment has already been completed."
                          : errorType === "taken"
                            ? "that the Remote Online Notarization (RON) appointment you selected has already been accepted by you or another notary."
                            : errorType === "not_found" &&
                                errorMessage ===
                                  "You can not accept this job this time. The RON session is expired or does not exist"
                              ? "that the RON session you trying to accept is expired or does not exist."
                              : errorType === "not_found"
                                ? "that we were unable to find and validate your notary account."
                                : errorMessage ===
                                    "You can not accept this job this time. Failed to start RON session"
                                  ? "that we encountered an issue during the process of processing and initiating the RON session for the signee."
                                  : errorMessage ===
                                      "You can not accept this job this time. Job has been cancelled or doesn't exist"
                                    ? "that the RON session has been cancelled or doesn't exist."
                                    : "that there was an issue processing your acceptance. Please try again or contact support."
                      } We appreciate your prompt response and encourage you to log into your dashboard for other available notarization opportunities.`
                    )}
                  </Typography>

                  {errorType !== "processing" && (
                    <Grid container justifyContent="center">
                      <Grid size={{ xs: 12 }}>
                        <Button
                          onClick={() => navigate("/")}
                          fullWidth
                          variant="contained"
                        >
                          BACK TO MAIN MENU
                        </Button>
                      </Grid>
                    </Grid>
                  )}
                </>
              )}
            </Box>
          ) : (
            // ===== REVIEW STATE (FIXED HEIGHT + SCROLL + STICKY HEADER/FOOTER) =====
            <Card elevation={0} sx={mainCardSx}>
              {/* Sticky Header (logo + title + subtitle) */}
              <Box
                sx={{
                  position: "sticky",
                  top: 0,
                  zIndex: 5,
                  bgcolor: "#fff",
                  borderBottom: "1px solid",
                  borderColor: "divider",
                }}
              >
                <Box sx={{ px: { xs: 2, sm: 3 }, pt: 2, pb: 2 }}>
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "center",
                      mb: 1.5,
                      cursor: "pointer",
                    }}
                    onClick={() => navigate("/")}
                  >
                    <img
                      style={{
                        maxWidth: mobileSize
                          ? "90%"
                          : tabletSize
                            ? "50%"
                            : "10rem",
                        height: "auto",
                      }}
                      alt="Permit Rockstar Logo"
                      src="/img/logos/logo_main.png"
                    />
                  </Box>

                  <Stack spacing={0.8} alignItems="center">
                    <Typography
                      color="primary"
                      variant={mobileSize ? "h5" : "h4"}
                      sx={{ fontWeight: 800, lineHeight: 1.1 }}
                      align="center"
                    >
                      Review Notarization Request
                    </Typography>
                    <Typography
                      color="text.secondary"
                      variant="body1"
                      align="center"
                      sx={{ maxWidth: 760 }}
                    >
                      Confirm the signer details and select authentication
                      before accepting this RON appointment.
                    </Typography>
                  </Stack>
                </Box>
              </Box>

              {/* Scrollable Content */}
              <Box
                sx={{
                  flex: 1,
                  overflowY: "auto",
                  px: { xs: 2, sm: 3 },
                  py: 2,
                  bgcolor: "#fff",
                }}
              >
                {/* Signer Details */}
                <Card
                  elevation={0}
                  sx={{
                    mb: 2.5,
                    borderRadius: 3,
                    border: "1px solid",
                    borderColor: "divider",
                    overflow: "hidden",
                    position: "relative",
                  }}
                >
                  <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
                    <Stack
                      direction="row"
                      alignItems="center"
                      justifyContent="space-between"
                      sx={{ mb: 2 }}
                    >
                      <Typography
                        color="primary"
                        variant="h6"
                        sx={{ fontWeight: 800 }}
                      >
                        Signer Details
                      </Typography>
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                        }}
                        gap={1}
                      >
                        <img
                          src="/img/website_img/building_icon.svg"
                          alt="building"
                          width="20px"
                        />
                        <Typography
                          fontWeight="bold"
                          variant="body2"
                          color="primary"
                        >
                          {signerDetails?.project_data?.projectAddress}
                        </Typography>
                      </Box>
                    </Stack>

                    <Grid container spacing={2} alignItems="center">
                      <Grid size={{ xs: 12 }}>
                        <Stack
                          alignItems="center"
                          justifyContent="center"
                          spacing={1}
                          sx={{
                            p: 2,
                            borderRadius: 3,
                            border: "1px dashed",
                            borderColor: "divider",
                            bgcolor: "action.hover",
                            minHeight: 160,
                            justifyContent: "center",
                          }}
                        >
                          <Chip
                            label={
                              signerType.toLowerCase() === "owner"
                                ? "Property Owner"
                                : "Contractor"
                            }
                            variant="outlined"
                            color="primary"
                            sx={{
                              fontWeight: 700,
                              position: "absolute",
                              top: 50,
                              right: 15,
                              backgroundColor: "white",
                            }}
                          />

                          <Box
                            sx={{
                              display: "flex",
                              width: "100%",
                              height: "100%",
                            }}
                          >
                            <Box sx={{ textAlign: "center", flex: 2 }}>
                              <img
                                src="/img/website_img/profile_placeholder.png"
                                alt="profile-placeholder"
                                width="15%"
                              />
                              <Typography sx={{ fontWeight: "bold" }}>
                                {fullName}
                              </Typography>
                              <Typography sx={{ fontWeight: "bold" }}>
                                {email}
                              </Typography>
                              <Typography sx={{ fontWeight: "bold" }}>
                                {phone}
                              </Typography>
                            </Box>
                            <Box sx={{ alignSelf: "flex-end", flex: 1 }}>
                              <FormControl>
                                <Typography
                                  color="text.secondary"
                                  variant="body2"
                                >
                                  Personally known?
                                </Typography>

                                <RadioGroup
                                  row
                                  value={personallyKnown}
                                  onChange={(event) =>
                                    setPersonallyKnown(event.target.value)
                                  }
                                >
                                  <FormControlLabel
                                    value="yes"
                                    control={<Radio />}
                                    label="Yes"
                                  />
                                  <FormControlLabel
                                    value="no"
                                    control={<Radio />}
                                    label="No"
                                  />
                                </RadioGroup>

                                <Typography
                                  variant="body2"
                                  color="text.secondary"
                                >
                                  {personallyKnown === "yes"
                                    ? "Authentication will be skipped for this signer."
                                    : "Biometrics authentication will be required."}
                                </Typography>
                              </FormControl>
                            </Box>
                          </Box>
                        </Stack>
                      </Grid>
                    </Grid>
                  </CardContent>
                </Card>

                {/* Past Notarizations */}
                <Card
                  elevation={0}
                  sx={{
                    mb: 2.5,
                    borderRadius: 3,
                    border: "1px solid",
                    borderColor: "divider",
                  }}
                >
                  <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
                    <Typography
                      color="primary"
                      variant="h6"
                      sx={{ fontWeight: 800, mb: 2 }}
                    >
                      Past Notarizations
                    </Typography>

                    {rows.length === 0 ? (
                      <Typography color="text.secondary" variant="body2">
                        No prior notarizations found for this signer.
                      </Typography>
                    ) : (
                      <Box
                        sx={{
                          height: 360,
                          width: "100%",
                          "& .MuiDataGrid-columnHeaders": {
                            bgcolor: "action.hover",
                            borderBottom: "1px solid",
                            borderColor: "divider",
                          },
                          "& .MuiDataGrid-cell": {
                            borderColor: "divider",
                          },
                          "& .MuiDataGrid-root": {
                            borderRadius: 2,
                          },
                        }}
                      >
                        <DataGrid
                          rows={rows}
                          columns={columns}
                          disableRowSelectionOnClick
                          pageSizeOptions={[5, 10, 25]}
                          initialState={{
                            pagination: {
                              paginationModel: { pageSize: 5, page: 0 },
                            },
                          }}
                        />
                      </Box>
                    )}
                  </CardContent>
                </Card>

                {/* A bit of bottom breathing room so last card doesn't hide behind footer */}
                <Box sx={{ height: 10 }} />
              </Box>

              {/* Sticky Footer (Accept button) */}
              <Box
                sx={{
                  position: "sticky",
                  bottom: 0,
                  zIndex: 5,
                  bgcolor: "#fff",
                  borderTop: "1px solid",
                  borderColor: "divider",
                  px: { xs: 2, sm: 3 },
                  py: 2,
                }}
              >
                <LoadingButton
                  loading={acceptLoading}
                  onClick={handleAcceptInstant}
                  variant="contained"
                  size="large"
                  fullWidth
                  sx={{
                    borderRadius: 999,
                    py: 1.3,
                    fontWeight: 800,
                    maxWidth: tabletSize ? "100%" : 520,
                    mx: "auto",
                    display: "block",
                  }}
                >
                  Accept Notarization
                </LoadingButton>
              </Box>
            </Card>
          )}
        </Box>
      )}
    </>
  );
};

export default AcceptInstant;
